import React from 'react';
import { useTranslation } from "react-i18next";
import styles from './LotValueGraph.module.scss';
import { CompanyPage, CompanyPageBid } from '../../../../../../hooks/slices/companySlice'; // Adjusted
import { ScatterGraphStacked, DataStackedAttributes } from '../../../../../../components/graphs/scatterGraph'; // Adjusted
import { formatNumberShort } from '../../../../../../utils/format'; // Adjusted
import {
    calculateMetricByYear,
    calculateYoYChange,
    getSimpleYoYSubtitleProps,
    reduceBidsByYear,
    SimpleYoYSubtitleResult // Ensure exported
} from '../../../info/infoUtils'; // Adjusted relative path

interface LotValueGraphProps {
    companyPage: CompanyPage;
}

export const LotValueGraph: React.FC<LotValueGraphProps> = (props) => {
    const { t } = useTranslation();
    const companyPage: CompanyPage = props.companyPage;
    const bids: CompanyPageBid[] = companyPage.bids ?? [];

    // Filter bids for calculation (winners with value)
    const bidsWithValues = bids.filter(bid => bid.bidValue !== null && bid.bidRole === "WINNER");

    // Calculate year-over-year change
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;
    const relevantYears = [lastYear, currentYear];

    const yearlyBids = reduceBidsByYear(bidsWithValues); // Group filtered bids

    // Calculate average bid value per year
    const yearlyAverageValue = calculateMetricByYear<number>(
        yearlyBids,
        relevantYears,
        (bidsInYear: CompanyPageBid[]) => {
            const totalValue = bidsInYear.reduce((sum, bid) => sum + (bid.bidValue ?? 0), 0);
            return bidsInYear.length > 0 ? totalValue / bidsInYear.length : 0; // Avoid division by zero
        },
        0 // Default average is 0
    );

    const currentYearAverage = yearlyAverageValue[currentYear.toString()];
    const lastYearAverage = yearlyAverageValue[lastYear.toString()];

    // Use new YoY helper for percentage change
    const yearOverYearChange = calculateYoYChange(currentYearAverage, lastYearAverage, 'percentageChange');

    const hasSufficientData = yearOverYearChange !== null; // Sufficient if change could be calculated

    const subtitleProps: SimpleYoYSubtitleResult | null = hasSufficientData ? getSimpleYoYSubtitleProps({
        yearOverYearChange: yearOverYearChange!,
        t: t,
        increaseKey: "companyPage.lot_value_increase",
        decreaseKey: "companyPage.lot_value_decrease",
        noChangeKey: "companyPage.lot_value_no_change",
    }) : null;

    const changeText = hasSufficientData ? Math.abs(yearOverYearChange!).toFixed(1) : '';

    const formatCurrency = (value: number): string => {
        const formatted = formatNumberShort(value, { invalidInputReturn: '0' }) ?? '0';
        return formatted + ' €';
    };

    // Prepare data for ScatterGraph: Map each bid to a point
    const individualBidPoints = bidsWithValues.map(bid => {
        const date = new Date(bid.awardDate ?? bid.deadline ?? bid.publicationDate ?? Date.now()); // Use awardDate or deadline or publicationDate or now
        return {
            x: date.getTime(), // Use timestamp for numerical x-axis
            y: bid.bidValue ?? 0, // Bid value for y-axis
            label: `${formatCurrency(bid.bidValue ?? 0)} on ${date.toLocaleDateString()}` // Tooltip label
        };
    }).sort((a, b) => a.x - b.x); // Sort by timestamp (date)

    // Group bids by timestamp to calculate average and ensure one point per date
    const bidsByTimestamp: Record<number, number[]> = individualBidPoints.reduce((acc, point) => {
        if (!acc[point.x]) {
            acc[point.x] = [];
        }
        acc[point.x].push(point.y);
        return acc;
    }, {} as Record<number, number[]>);

    // Create stackedData with one entry per timestamp, using the average value
    const stackedData: DataStackedAttributes[] = Object.entries(bidsByTimestamp).map(([timestampStr, values]) => {
        const timestamp = Number(timestampStr);
        const averageValue = values.reduce((sum, val) => sum + val, 0) / values.length;
        return {
            x: timestamp,
            y: [averageValue], // Only the average value in the y-array
            label: [t('companyPage.lotValueLabel', 'Lot Value')] // Use translation key for the label array
        };
    });

    // Calculate average bid value over the last 5 years
    const currentYearForAvg = new Date().getFullYear();
    const fiveYearsAgo = currentYearForAvg - 4; // Inclusive of current year
    const relevantYearsForFiveYearAvg = Array.from({ length: 5 }, (_, i) => currentYearForAvg - i);

    const bidsLastFiveYears = bidsWithValues.filter(bid => {
        const awardDateStr = bid.awardDate;
        if (!awardDateStr) return false; // Require awardDate for this average
        const bidDate = new Date(awardDateStr);
        if (isNaN(bidDate.getTime())) return false; // Ensure valid date
        const bidYear = bidDate.getFullYear();
        return relevantYearsForFiveYearAvg.includes(bidYear);
    });

    const totalValueLastFiveYears = bidsLastFiveYears.reduce((sum, bid) => sum + (bid.bidValue ?? 0), 0);
    const averageValueLastFiveYears = bidsLastFiveYears.length > 0
        ? totalValueLastFiveYears / bidsLastFiveYears.length
        : (companyPage.averageBidValue ?? 0); // Fallback if no bids in last 5 years

    return (
        <div className={`${styles["graph-card"]} ${styles["lotvalue-card"]}`}>
            <div className={styles["heading-container"]}>
                <span className={styles["graph-type-title"]}>{t("companyPage.lot_value_graph_type_scatter", "Lot Values")}</span>
                <h3 className={styles["last-year-percentage"]}>{`${formatCurrency(averageValueLastFiveYears)}`}</h3>
                {/* {hasSufficientData && subtitleProps ? (
                    <div className={`${styles.subtitle}`}>
                        <span className={`${styles["change-indicator"]} ${styles[`change-indicator-${subtitleProps.colorSuffix}`]}`}>
                            {`${subtitleProps.trendIcon}${changeText}% ${subtitleProps.changeDescription}`}
                        </span>
                    </div>
                ) : (
                    <div className={styles.subtitle}>
                        {t("companyPage.lot_value_title_2", "vs last year")} 
                    </div>
                )} */}
            </div>
            <div className={styles["graph-container"]}>
                <ScatterGraphStacked 
                    data={stackedData} 
                    showXAxis={false}
                    showYAxis={true}
                    showTooltip={true}
                    showLegend={false}
                    // Pass domains explicitly if needed by ScatterGraphStacked
                    // xDomain={xDomainScatter}
                    // yDomain={yDomainScatter}
                />
            </div>
        </div>
    );
} 